import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "react-toastify";

import { useCreateDeveloperApiToken } from "api/queries/user/useCreateDeveloperApiToken";
import { ReactComponent as KeyTokenIcon } from "assets/images/icons/KeyToken.svg";
import { ReactComponent as CopyIcon } from "assets/images/iconsV2/copy.svg";
import Button from "components/v2/Button/Button";
import Input from "components/v2/Input/Input";
import Modal from "components/v2/Modal/Modal";

type Step = "name" | "reveal";

interface GenerateTokenModalProps {
  open: boolean;
  onClose: () => void;
}

interface GenerateTokenFormValues {
  tokenName: string;
}

export default function GenerateTokenModal({ open, onClose }: GenerateTokenModalProps) {
  const intl = useIntl();
  const [step, setStep] = useState<Step>("name");
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const methods = useForm<GenerateTokenFormValues>({
    defaultValues: { tokenName: "" },
  });

  const { mutate: createToken, isLoading } = useCreateDeveloperApiToken();

  useEffect(() => {
    if (open) {
      setStep("name");
      methods.reset({ tokenName: "" });
      setRevealedToken(null);
      setCopied(false);
    }
  }, [open, methods]);

  const handleGenerate = methods.handleSubmit(({ tokenName }) => {
    createToken(
      { name: tokenName.trim() },
      {
        onSuccess: (data) => {
          setRevealedToken(data.token);
          setStep("reveal");
        },
        onError: () => {
          toast(
            intl.formatMessage({
              id: "settings.developerApiTokens.createError",
              defaultMessage: "Failed to generate the token. Please try again.",
            }),
            { type: "error" },
          );
        },
      },
    );
  });

  const handleCopy = () => {
    if (!revealedToken) return;
    navigator.clipboard
      .writeText(revealedToken)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(() => {
        toast(intl.formatMessage({ id: "general.copyFailed", defaultMessage: "Failed to copy." }), { type: "error" });
      });
  };

  const handleClose = () => {
    setRevealedToken(null);
    onClose();
  };

  return (
    <Modal show={open} onHide={handleClose} size="xl">
      <Modal.Header>
        <div className="text-xl font-semibold text-primary">
          <FormattedMessage id="settings.developerApiTokens.modal.title" defaultMessage="Generate token" />
        </div>
      </Modal.Header>

      <Modal.Body className={step === "reveal" ? "pt-3xl" : "pt-4xl"}>
        {step === "name" ? (
          <FormProvider {...methods}>
            <Input
              name="tokenName"
              label={intl.formatMessage({
                id: "settings.developerApiTokens.modal.tokenName",
                defaultMessage: "Token name",
              })}
              required
              registerOptions={{
                validate: (value: string) =>
                  value?.trim()
                    ? true
                    : intl.formatMessage({
                        id: "settings.developerApiTokens.modal.nameRequired",
                        defaultMessage: "Token name is required.",
                      }),
              }}
            />
          </FormProvider>
        ) : (
          <>
            <div className="d-flex justify-content-center align-items-center py-4xl bg-primary">
              <KeyTokenIcon width={220} height={220} />
            </div>

            <div
              className="d-flex align-items-center gap-md w-100 py-sm pl-sm pr-lg radius-full bg-warning-primary border-1 border-solid mb-xl"
              style={{ borderColor: "#fdb022" }}
            >
              <span
                className="text-xs font-medium fg-warning-primary bg-warning-primary radius-full border-1 border-solid flex-none py-xxs px-md no-wrap"
                style={{ borderColor: "#fdb022" }}
              >
                <FormattedMessage id="settings.developerApiTokens.modal.reminderLabel" defaultMessage="Reminder" />
              </span>
              <span className="text-xs no-wrap" style={{ color: "#b54708" }}>
                <FormattedMessage
                  id="settings.developerApiTokens.modal.reminderText"
                  defaultMessage="This is the only time you will have access to the full token. Save it securely."
                />
              </span>
            </div>

            <div
              className="d-flex align-items-start w-100 border-1 border-solid border-primary radius-xs bg-primary mb-3xl"
              style={{ minHeight: 40 }}
            >
              <div className="flex-1 text-md text-primary py-md px-lg break-all" style={{ minWidth: 0, userSelect: "all" }}>
                {revealedToken || ""}
              </div>
              <Button
                label={
                  copied
                    ? intl.formatMessage({ id: "general.copied", defaultMessage: "Copied" })
                    : intl.formatMessage({ id: "general.copy", defaultMessage: "Copy" })
                }
                iconLeft={<CopyIcon width={16} height={16} />}
                variant="link-gray"
                size="sm"
                className="generate-token-copy-btn"
                onClick={handleCopy}
              />
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex align-items-center justify-content-end gap-3xl w-100">
          <Button
            label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
            variant="link-color"
            onClick={handleClose}
          />
          {step === "name" ? (
            <Button
              label={intl.formatMessage({
                id: "settings.developerApiTokens.modal.generate",
                defaultMessage: "Generate",
              })}
              disabled={isLoading}
              onClick={handleGenerate}
            />
          ) : (
            <Button
              label={intl.formatMessage({ id: "general.done", defaultMessage: "Done" })}
              onClick={handleClose}
            />
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
